import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import { useAppSelector } from '../../store/hooks';
import { Ingredient } from '../../types/types';

export interface RecipeFormData {
  name: string;
  description: string;
  instructions: string;
  ingredients_attributes: Ingredient[];
  visibility: 'public' | 'private';
}

interface RecipeFormProps {
  formData: RecipeFormData;
  setFormData: (data: RecipeFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitButtonText: string;
}

type IngredientField = 'food_id' | 'food_name' | 'measurement';

const WarningIcon = () => (
  <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const inputClass = "w-full px-3 py-2 bg-army-900 border border-army-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-neon focus:ring-0 transition-colors";
const labelClass = "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5";

const ToolbarBtn = ({ onClick, active, children }: { onClick: () => void; active: boolean; children: React.ReactNode }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${active ? 'bg-army-800 text-neon' : 'text-gray-500 hover:text-white hover:bg-army-800'}`}
  >
    {children}
  </button>
);

const RecipeForm: React.FC<RecipeFormProps> = ({ formData, setFormData, onSubmit, submitButtonText }) => {
  const { items: foods } = useAppSelector((state) => state.ingredients);

  const editor = useEditor({
    extensions: [StarterKit, Markdown],
    content: formData.instructions || '',
    onUpdate: ({ editor }) => {
      setFormData({ ...formData, instructions: editor.storage.markdown.getMarkdown() });
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.storage.markdown.getMarkdown();
    if ((formData.instructions || '') !== current) {
      editor.commands.setContent(formData.instructions || '');
    }
  }, [formData.instructions, editor]);

  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients_attributes: [...formData.ingredients_attributes, { food_id: 0, measurement: '' }]
    });
  };

  const deleteIngredient = (indexToDelete: number) => {
    setFormData({
      ...formData,
      ingredients_attributes: formData.ingredients_attributes
        .map((ingredient, index) => {
          if (index !== indexToDelete) return ingredient;
          if (ingredient.id) return { ...ingredient, _destroy: true };
          return null;
        })
        .filter((i): i is Ingredient => i !== null)
    });
  };

  const handleIngredientChange = (index: number, field: IngredientField, value: string | number) => {
    const newIngredients = [...formData.ingredients_attributes];
    newIngredients[index] = {
      id: newIngredients[index]?.id,
      food_id: newIngredients[index]?.food_id || 0,
      food_name: newIngredients[index]?.food_name || '',
      measurement: newIngredients[index]?.measurement || '',
      [field]: value
    };
    setFormData({ ...formData, ingredients_attributes: newIngredients });
  };

  const getFoodSuggestions = (searchTerm: string) => {
    const term = searchTerm?.toLowerCase().trim() || '';
    if (foods.some(f => f.name.toLowerCase() === term)) return [];
    return foods
      .filter(f => f.name.toLowerCase().includes(term))
      .map(f => <option key={f.id} value={f.name} />);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <label className={labelClass}>Recipe Name</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className={inputClass}
          placeholder="e.g. Chicken Tikka Masala"
        />
      </div>

      <div>
        <label className={labelClass}>Description</label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className={`${inputClass} resize-none`}
          rows={2}
          placeholder="A short description..."
        />
      </div>

      <div>
        <label className={labelClass}>Instructions</label>
        <div className="bg-army-900 border border-army-800 rounded-lg overflow-hidden focus-within:border-neon transition-colors">
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-army-800">
            <ToolbarBtn onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold') ?? false}><b>B</b></ToolbarBtn>
            <ToolbarBtn onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive('italic') ?? false}><i>I</i></ToolbarBtn>
            <span className="text-army-700 px-0.5">|</span>
            <ToolbarBtn onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} active={editor?.isActive('heading', { level: 2 }) ?? false}>H2</ToolbarBtn>
            <span className="text-army-700 px-0.5">|</span>
            <ToolbarBtn onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive('orderedList') ?? false}>1.</ToolbarBtn>
            <ToolbarBtn onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive('bulletList') ?? false}>•</ToolbarBtn>
          </div>
          <EditorContent editor={editor} />
        </div>
      </div>

      <div>
        <label className={labelClass}>Ingredients</label>
        <div className="space-y-2">
          {formData.ingredients_attributes.map((ingredient, index) => (
            <div key={index} className={`flex gap-2 relative hover:z-10 ${ingredient._destroy ? 'hidden' : ''}`}>
              <div className="relative flex-1">
                <input
                  type="text"
                  value={ingredient.food_name}
                  onChange={(e) => {
                    const searchTerm = e.target.value.toLowerCase();
                    const matchedFood = foods.find(f => f.name.toLowerCase() === searchTerm);
                    const newIngredients = [...formData.ingredients_attributes];
                    newIngredients[index] = { ...ingredient, food_name: e.target.value, food_id: matchedFood ? matchedFood.id : 0 };
                    setFormData({ ...formData, ingredients_attributes: newIngredients });
                  }}
                  list={`foods-list-${index}`}
                  className={`w-full px-3 py-2 bg-army-900 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-neon transition-colors ${
                    !ingredient.food_id && ingredient.food_name ? 'border-yellow-800' : 'border-army-800'
                  }`}
                  placeholder="Food name..."
                />
                {!ingredient.food_id && ingredient.food_name && (
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2 group">
                    <WarningIcon />
                    <div className="absolute right-0 top-6 w-52 p-2 bg-navy-900 text-yellow-500 text-xs rounded-lg shadow-xl border border-navy-800 invisible group-hover:visible z-50">
                      New food type will be created. Select from suggestions if possible.
                    </div>
                  </div>
                )}
                <datalist id={`foods-list-${index}`}>
                  {getFoodSuggestions(ingredient.food_name || '')}
                </datalist>
              </div>

              <input
                type="text"
                value={ingredient.measurement}
                onChange={(e) => handleIngredientChange(index, 'measurement', e.target.value)}
                placeholder="e.g. 2 cups"
                className="px-3 py-2 bg-army-900 border border-army-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-neon w-36 transition-colors"
              />

              <button
                type="button"
                onClick={() => deleteIngredient(index)}
                className="px-3 py-2 bg-army-900 hover:bg-rose-950 text-army-700 hover:text-rose-500 border border-army-800 hover:border-rose-900 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addIngredient}
          className="mt-3 px-4 py-2 text-sm bg-transparent hover:bg-army-900 text-gray-500 hover:text-white border border-army-800 rounded-lg transition-colors"
        >
          + Add Ingredient
        </button>
      </div>

      <div>
        <label className={labelClass}>Visibility</label>
        <div className="flex gap-3">
          {(['private', 'public'] as const).map((v) => (
            <label key={v} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="visibility"
                value={v}
                checked={formData.visibility === v}
                onChange={() => setFormData({ ...formData, visibility: v })}
                className="accent-neon"
              />
              <span className="text-sm text-gray-300 capitalize">{v}</span>
            </label>
          ))}
        </div>
        <p className="mt-1 text-xs text-gray-600">
          {formData.visibility === 'private'
            ? 'Only you (and people you share with) can see this recipe.'
            : 'Anyone can see this recipe in the public list.'}
        </p>
      </div>

      <div className="pt-1">
        <button
          type="submit"
          className="px-6 py-2.5 bg-neon hover:brightness-90 text-army-950 font-bold rounded-lg transition-all"
        >
          {submitButtonText}
        </button>
      </div>
    </form>
  );
};

export default RecipeForm;
